import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {Link, useNavigate} from "react-router-dom";
import {RootState, useAppDispatch} from "../../../../redux/store";

// Import slice e types - PATH RELATIVI
import {
    fetchOrdiniLavoro,
    deleteOrdineLavoro,
    OrdiniLavoroState,
} from "./ordiniLavoroSlice";

import {
    OrdineLavoro,
    OrdiniLavoroList,
    STATI_ORDINE_LAVORO,
    getStatoOrdineLavoroColor,
    isOrdineLavoroInRitardo,
} from "./ordiniLavoroTypes";

import {
    FiltriOrdiniLavoro,
    setFiltriOrdiniLavoro,
    resetFiltriOrdiniLavoro,
} from "components/userPreference/userPreferenceSlice";

import {ErrorStatusTypes} from "components/common/errorsDeclarations";
import {Routes_path} from "components/routerApp";
import {convertToDateTime} from "components/common/dateUtils/convertToDateTime";

// Anagrafiche
import {Commerciale, Soggetto, Stagione, Tecnica} from "components/anagrafiche/anagraficheSlice";

// Material UI
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Pagination from "@mui/material/Pagination";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import {useTheme} from "@mui/material/styles";
import {DesktopDatePicker} from "@mui/x-date-pickers/DesktopDatePicker";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import WorkIcon from "@mui/icons-material/Work";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ScheduleIcon from "@mui/icons-material/Schedule";

// Breadcrumbs
import OrdiniLavoroPageBreadcrumbs from "./OrdiniLavoroPageBreadcrumbs";
import AllegatiPreview from "../../../allegati/AllegatiPreview";

// ============================================
// COMPONENT
// ============================================

const OrdiniLavoroPage = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const theme = useTheme();

    // ============================================
    // SELECTORS
    // ============================================

    const ordiniLavoroList = useSelector<RootState, OrdiniLavoroList>(
        (state) => state.ordiniLavoroState.ordiniLavoroList
    );

    const filtri = useSelector<RootState, FiltriOrdiniLavoro>(
        (state) => state.userPreferenceState.filtriOrdiniLavoro || {}
    );

    // Anagrafiche
    const soggetti = useSelector<RootState, Soggetto[]>(
        (state) => state.anagraficheState.soggetti.results || []
    );
    const mandatari = soggetti.filter((s) => s.is_mandatario);
    const confezionisti = soggetti.filter((s) => s.is_confezionista);

    const stagioni = useSelector<RootState, Stagione[]>(
        (state) => state.anagraficheState.stagioni?.results || []
    );

    const commerciali = useSelector<RootState, Commerciale[]>(
        (state) => state.anagraficheState?.commerciali?.results || []
    );

    const tecniche = useSelector<RootState, Tecnica[]>(
        (state) => state.anagraficheState?.tecniche?.results || []
    );

    // ============================================
    // STATI LOCALI
    // ============================================

    const [searchValue, setSearchValue] = useState(filtri.search || "");
    const [refresh, setRefresh] = useState(false);
    const [showDateFilters, setShowDateFilters] = useState(false);

    // Stati filtri
    const [statiSelezionati, setStatiSelezionati] = useState<string[]>(
        filtri.stato || ['CONF', 'ARR']
    );
    const [stagioneSelezionata, setStagioneSelezionata] = useState<number | undefined>(
        filtri.stagione_id
    );
    const [mandatarioSelezionato, setMandatarioSelezionato] = useState<number | undefined>(
        filtri.mandatario_id
    );
    const [confezionistaSel, setConfezionistaSel] = useState<number | undefined>(
        filtri.confezionista_id
    );
    const [commercialeSel, setCommercialeSel] = useState<number | undefined>(
        filtri.commerciale_id
    );
    const [tecnicheSelezionate, setTecnicheSelezionate] = useState<number[]>(
        filtri.tecnica_id || []
    );

    // Filtri date
    const [dataDa, setDataDa] = useState<string | undefined>(
        filtri.data_da
    );
    const [dataA, setDataA] = useState<string | undefined>(
        filtri.data_a
    );

    // Menu anchors
    const [anchorElStatoFilter, setAnchorElStatoFilter] = useState<null | HTMLElement>(null);
    const [anchorElTecnicaFilter, setAnchorElTecnicaFilter] = useState<null | HTMLElement>(null);

    // ============================================
    // EFFECTS
    // ============================================

    useEffect(() => {
        dispatch(fetchOrdiniLavoro(filtri));
    }, []);

    useEffect(() => {
        if (refresh) {
            const nuoviFiltri: FiltriOrdiniLavoro = {
                ...filtri,
                search: searchValue || undefined,
                stato: statiSelezionati.length > 0 ? statiSelezionati : undefined,
                stagione_id: stagioneSelezionata,
                mandatario_id: mandatarioSelezionato,
                confezionista_id: confezionistaSel,
                commerciale_id: commercialeSel,
                tecnica_id: tecnicheSelezionate.length > 0 ? tecnicheSelezionate : undefined,
                data_da: dataDa,
                data_a: dataA,
                page: 1,
            };

            dispatch(setFiltriOrdiniLavoro(nuoviFiltri));
            stagioneSelezionata && dispatch(fetchOrdiniLavoro(nuoviFiltri));
            setRefresh(false);
        }
    }, [refresh]);

    // ============================================
    // HANDLERS
    // ============================================

    const handleResetFiltri = () => {
        setSearchValue("");
        setStatiSelezionati(['CONF', 'ARR']);
        // setStagioneSelezionata(undefined);
        setMandatarioSelezionato(undefined);
        setConfezionistaSel(undefined);
        setCommercialeSel(undefined);
        setTecnicheSelezionate([]);
        setDataDa(undefined);
        setDataA(undefined);

        dispatch(resetFiltriOrdiniLavoro());

        const filtriDefault: FiltriOrdiniLavoro = {
            numero_record: 20,
            page: 1,
            stato: ['CONF', 'ARR'],
            ordering: 'data_consegna_prevista',
        };
        stagioneSelezionata && dispatch(fetchOrdiniLavoro(filtriDefault));
    };

    const handlePaginationChange = (event: React.ChangeEvent<unknown>, page: number) => {
        const nuoviFiltri = {...filtri, page};
        dispatch(setFiltriOrdiniLavoro(nuoviFiltri));
        stagioneSelezionata && dispatch(fetchOrdiniLavoro(nuoviFiltri));
        window.scrollTo({top: 0, behavior: 'smooth'});
    };

    const handleRecordsPerPageChange = (event: SelectChangeEvent<number>) => {
        const numero_record = event.target.value as number;
        const nuoviFiltri = {
            ...filtri,
            numero_record,
            page: 1,
        };
        dispatch(setFiltriOrdiniLavoro(nuoviFiltri));
        stagioneSelezionata && dispatch(fetchOrdiniLavoro(nuoviFiltri));
    };

    const toggleStato = (stato: string) => {
        setStatiSelezionati((prev) =>
            prev.includes(stato)
                ? prev.filter((s) => s !== stato)
                : [...prev, stato]
        );
        setRefresh(true)
    };

    const toggleTecnica = (tecnicaId: number) => {
        setTecnicheSelezionate((prev) =>
            prev.includes(tecnicaId)
                ? prev.filter((t) => t !== tecnicaId)
                : [...prev, tecnicaId]
        );
        setRefresh(true);
    };

    const handleDelete = async (ordineLavoro: OrdineLavoro) => {
        if (window.confirm(`Eliminare l'ordine di lavoro #${ordineLavoro.id}?`)) {
            await dispatch(deleteOrdineLavoro({ordineLavoroToDelete: ordineLavoro}));
            dispatch(fetchOrdiniLavoro(filtri));
        }
    };

    // Ordinamento colonne
    const handleSort = (campo: string) => {
        let nuovoOrdering = campo;
        if (filtri.ordering === campo) {
            nuovoOrdering = `-${campo}`;
        } else if (filtri.ordering === `-${campo}`) {
            nuovoOrdering = campo;
        }

        const nuoviFiltri = {...filtri, ordering: nuovoOrdering, page: 1};
        dispatch(setFiltriOrdiniLavoro(nuoviFiltri));
        stagioneSelezionata && dispatch(fetchOrdiniLavoro(nuoviFiltri));
    };

    const getSortIcon = (campo: string) => {
        if (filtri.ordering === campo) {
            return <ArrowUpwardIcon fontSize="small"/>;
        }
        if (filtri.ordering === `-${campo}`) {
            return <ArrowDownwardIcon fontSize="small"/>;
        }
        return null;
    };

    // Formattazione data
    const formatDate = (date: string | Date | null | undefined): string => {
        if (!date) return "-";
        const dt = convertToDateTime(date);
        return dt ? dt.setLocale("it").toFormat("dd/MM/yyyy") : "-";
    };

    // Formattazione quantità con virgola
    const formatQta = (value: number | string | null | undefined): string => {
        if (value == null || value === "") return "-";
        const num = Number(value);
        if (isNaN(num)) return "-";
        return Intl.NumberFormat("it-IT", {
            style: "decimal",
            useGrouping: true,
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
        }).format(num);
    };

    // Conta filtri date attivi
    const countActiveDateFilters = (): number => {
        let count = 0;
        if (dataDa || dataA) count++;
        return count;
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <Box sx={{p: 2}}>
            {/* Header */}
            <Grid container spacing={2} sx={{mb: 2}}>
                <Grid size={{xs: 12, sm: 6}}>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                            justifyContent: "flex-start",
                            alignItems: "center",
                        }}
                    >
                        <OrdiniLavoroPageBreadcrumbs/>
                    </Stack>
                </Grid>

                <Grid size={{xs: 12, sm: 6}}>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{justifyContent: "flex-end"}}
                    >
                        {/* Ricerca */}
                        <FormControl sx={{minWidth: 250}} variant="outlined" size="small">
                            <InputLabel>Ricerca</InputLabel>
                            <OutlinedInput
                                type="text"
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setRefresh(true)}
                                            edge="end"
                                            size="small"
                                        >
                                            <SearchIcon/>
                                        </IconButton>
                                    </InputAdornment>
                                }
                                label="Ricerca"
                                placeholder="Codice, descrizione..."
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                onBlur={() => setRefresh(true)}
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                        setRefresh(true);
                                    }
                                }}
                            />
                        </FormControl>

                        {/* Bottone nuovo */}
                        {/*<Button*/}
                        {/*    variant="contained"*/}
                        {/*    startIcon={<AddIcon />}*/}
                        {/*    component={Link}*/}
                        {/*    to={Routes_path.ORDINE_LAVORO}*/}
                        {/*    state={{*/}
                        {/*        stagione_id: filtri.stagione_id,*/}
                        {/*        mandatario_id: filtri.mandatario_id,*/}
                        {/*    }}*/}
                        {/*>*/}
                        {/*    Nuovo*/}
                        {/*</Button>*/}
                    </Stack>
                </Grid>
            </Grid>

            {/* Filtri */}
            <Card sx={{mb: 2, p: 2, backgroundColor: theme.palette.grey[50]}}>
                <Grid container spacing={2} sx={{alignItems: "center"}}>
                    {/* RIGA 1: intestazioni - spazio vuoto sopra Stagione..Commerciale, poi titolo date + bottone sopra Da/A */}
                    <Grid size={{xs: 12, sm: 6, md: 3, lg: 2}}/>
                    <Grid size={{xs: 12, sm: 6, md: 3, lg: 2}}/>
                    <Grid size={{xs: 12, sm: 6, md: 3, lg: 2}}/>
                    <Grid size={{xs: 12, sm: 6, md: 3, lg: 2}}/>
                    <Grid size={{xs: 12, md: 6, lg: 4}}>
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{alignItems: "center", justifyContent: "space-between"}}
                        >
                            <Typography variant="subtitle2" sx={{whiteSpace: "nowrap", paddingLeft:1}}>
                                Data Arrivo (Effettiva o Prevista)
                            </Typography>
                            <Button
                                variant="contained"
                                color="info"
                                size="small"
                                onClick={() => setRefresh(true)}
                            >
                                Applica Filtri Date
                            </Button>
                        </Stack>
                    </Grid>

                    {/* RIGA 2: tutti i campi filtro sulla stessa linea */}
                    {/* Stagione */}
                    <Grid size={{xs: 12, sm: 6, md: 3, lg: 2}}>
                        <Autocomplete
                            value={stagioni.find((s) => s.id === stagioneSelezionata) || null}
                            options={stagioni}
                            getOptionLabel={(option) => option.descrizione || ""}
                            size="small"
                            onChange={(_, newValue) => {
                                setStagioneSelezionata(newValue?.id);
                                setRefresh(true);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    sx={{
                                        backgroundColor: stagioneSelezionata
                                            ? theme.palette.info.main + '14'
                                            : "inherit",
                                    }}
                                    label="Stagione"
                                />
                            )}
                        />
                    </Grid>

                    {/* Mandatario */}
                    <Grid size={{xs: 12, sm: 6, md: 3, lg: 2}}>
                        <Autocomplete
                            value={mandatari.find((m) => m.id === mandatarioSelezionato) || null}
                            options={mandatari}
                            getOptionLabel={(option) => option.nome || ""}
                            size="small"
                            onChange={(_, newValue) => {
                                setMandatarioSelezionato(newValue?.id);
                                setRefresh(true);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    sx={{
                                        backgroundColor: mandatarioSelezionato
                                            ? theme.palette.info.main + '14'
                                            : "inherit",
                                    }}
                                    label="Mandatario"
                                />
                            )}
                        />
                    </Grid>

                    {/* Confezionista */}
                    <Grid size={{xs: 12, sm: 6, md: 3, lg: 2}}>
                        <Autocomplete
                            value={confezionisti.find((c) => c.id === confezionistaSel) || null}
                            options={confezionisti}
                            getOptionLabel={(option) => option.nome || ""}
                            size="small"
                            onChange={(_, newValue) => {
                                setConfezionistaSel(newValue?.id);
                                setRefresh(true);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    sx={{
                                        backgroundColor: confezionistaSel
                                            ? theme.palette.info.main + '14'
                                            : "inherit",
                                    }}
                                    label="Confezionista"
                                />
                            )}
                        />
                    </Grid>

                    {/* Commerciale */}
                    <Grid size={{xs: 12, sm: 6, md: 3, lg: 2}}>
                        <Autocomplete
                            value={commerciali.find((c) => c.id === commercialeSel) || null}
                            options={commerciali}
                            getOptionLabel={(option) => option.user_display || ""}
                            size="small"
                            onChange={(_, newValue) => {
                                setCommercialeSel(newValue?.id);
                                setRefresh(true);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    sx={{
                                        backgroundColor: commercialeSel
                                            ? theme.palette.info.main + '14'
                                            : "inherit",
                                    }}
                                    label="Commerciale"
                                />
                            )}
                        />
                    </Grid>

                    {/* Da */}
                    <Grid size={{xs: 6, md: 3, lg: 2}}>
                        <DesktopDatePicker
                            label="Da"
                            value={dataDa ? convertToDateTime(dataDa) : null}
                            onChange={(newValue) => {
                                setDataDa(
                                    newValue
                                        ? convertToDateTime(newValue)?.toFormat("yyyy-MM-dd")
                                        : undefined
                                );
                            }}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    fullWidth: true,
                                    sx: {
                                        backgroundColor: dataDa
                                            ? theme.palette.info.main + '14'
                                            : "inherit",
                                    },
                                },
                                field: {clearable: true},
                            }}
                        />
                    </Grid>

                    {/* A */}
                    <Grid size={{xs: 6, md: 3, lg: 2}}>
                        <DesktopDatePicker
                            label="A"
                            value={dataA ? convertToDateTime(dataA) : null}
                            onChange={(newValue) => {
                                setDataA(
                                    newValue
                                        ? convertToDateTime(newValue)?.toFormat("yyyy-MM-dd")
                                        : undefined
                                );
                            }}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    fullWidth: true,
                                    sx: {
                                        backgroundColor: dataA
                                            ? theme.palette.info.main + '14'
                                            : "inherit",
                                    },
                                },
                                field: {clearable: true},
                            }}
                        />
                    </Grid>

                    {/* RIGA 3: chips + reset */}
                    <Grid size={{xs: 12}}>
                        <Stack direction="row" spacing={2} sx={{justifyContent: "flex-start", alignItems: "center"}}>
                            {statiSelezionati.map((stato) => (
                                <Chip
                                    key={stato}
                                    label={STATI_ORDINE_LAVORO[stato as keyof typeof STATI_ORDINE_LAVORO]}
                                    color={getStatoOrdineLavoroColor(stato)}
                                    size="small"
                                    onDelete={() => {
                                        toggleStato(stato);
                                        setRefresh(true);
                                    }}
                                />
                            ))}
                            {tecnicheSelezionate.map((tecnicaId) => {
                                const tecnica = tecniche.find((t) => t.id === tecnicaId);
                                return tecnica ? (
                                    <Chip
                                        key={tecnicaId}
                                        label={tecnica.nome}
                                        color="info"
                                        size="small"
                                        onDelete={() => toggleTecnica(tecnicaId)}
                                    />
                                ) : null;
                            })}
                            <Tooltip title="Reset filtri">
                                <IconButton onClick={handleResetFiltri} color="info">
                                    <FilterAltOffIcon/>
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Grid>
                </Grid>
            </Card>

            {/* Header tabella */}
            {stagioneSelezionata &&
                <Card sx={{backgroundColor: theme.palette.primary.dark + "30", mb: 1}}>
                    <CardContent sx={{py: 1, "&:last-child": {pb: 1}}}>
                        <Grid container spacing={1} sx={{alignItems: "center"}}>
                            <Grid size={{xs: 0.5}}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    #
                                </Typography>
                            </Grid>
                            <Grid size={{xs: 2.3}}>
                                <Stack
                                    direction="row"
                                    sx={{alignItems: "center", cursor: "pointer"}}
                                    onClick={() => handleSort("previsione__codice_item")}
                                >
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Articolo
                                    </Typography>
                                    {getSortIcon("previsione__codice_item")}
                                </Stack>
                            </Grid>
                            <Grid size={{xs: 0.7}}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    Stagione
                                </Typography>
                            </Grid>
                            <Grid size={{xs: 1}}>
                                <Stack
                                    direction="row"
                                    sx={{alignItems: "center", cursor: "pointer"}}
                                    onClick={() => handleSort("previsione__mandatario__nome")}
                                >
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Mandatario
                                    </Typography>
                                    {getSortIcon("previsione__mandatario__nome")}
                                </Stack>
                            </Grid>
                            <Grid size={{xs: 1}}>
                                <Stack direction="row" sx={{alignItems: "center"}}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Tecnica
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => setAnchorElTecnicaFilter(e.currentTarget)}
                                        sx={{
                                            color: tecnicheSelezionate.length > 0
                                                ? "info.main"
                                                : "inherit",
                                        }}
                                    >
                                        <FilterAltIcon fontSize="small"/>
                                    </IconButton>
                                </Stack>
                            </Grid>
                            <Grid size={{xs: 1}}>
                                <Stack
                                    direction="row"
                                    sx={{alignItems: "center", cursor: "pointer"}}
                                    onClick={() => handleSort("stato")}
                                >
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Stato
                                    </Typography>
                                    {getSortIcon("stato")}
                                    <IconButton
                                        size="small"
                                        onClick={(e) => setAnchorElStatoFilter(e.currentTarget)}
                                        sx={{
                                            color: statiSelezionati.length > 0
                                                ? "info.main"
                                                : "inherit",
                                        }}
                                    >
                                        <FilterAltIcon fontSize="small"/>
                                    </IconButton>
                                </Stack>
                            </Grid>
                            <Grid size={{xs: 1}}>
                                <Stack
                                    direction="row"
                                    sx={{alignItems: "center", cursor: "pointer"}}
                                    onClick={() => handleSort("qta_prevista")}
                                >
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Qtà
                                    </Typography>
                                    {getSortIcon("qta_prevista")}
                                </Stack>
                            </Grid>
                            <Grid size={{xs: 1}} >
                                <Stack
                                    direction="row"
                                    sx={{alignItems: "center", cursor: "pointer"}}
                                    onClick={() => handleSort("data_arrivo_prevista")}
                                >
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Arrivo
                                    </Typography>
                                    {getSortIcon("data_arrivo_prevista")}
                                </Stack>
                            </Grid>
                            <Grid size={{xs: 1}} >
                                <Stack
                                    direction="row"
                                    sx={{alignItems: "center", cursor: "pointer"}}
                                    onClick={() => handleSort("data_consegna_prevista")}
                                >
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Consegna
                                    </Typography>
                                    {getSortIcon("data_consegna_prevista")}
                                </Stack>
                            </Grid>
                            <Grid size={{xs: 0.5}} >
                                <Typography variant="subtitle2" fontWeight="bold">
                                    Ritardo
                                </Typography>
                            </Grid>
                            <Grid size={{xs: 1}} >
                                <Typography variant="subtitle2" fontWeight="bold">
                                    Q.ta difforme
                                </Typography>
                            </Grid>
                            <Grid size={{xs: 1}} >
                                <Typography variant="subtitle2" fontWeight="bold">
                                    Azioni
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            }

            {/* Lista */}
            {stagioneSelezionata &&
                <Box>
                    {ordiniLavoroList.results.map((ordineLavoro) => (
                        <Card
                            key={ordineLavoro.id}
                            sx={{
                                mb: 1,
                                backgroundColor: ordineLavoro.assegnazioni && ordineLavoro.assegnazioni.length > 0
                                    ? theme.palette.success.main + '40'
                                    : theme.palette.grey[50],
                                transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: theme.shadows[4],
                                },
                                borderLeft: isOrdineLavoroInRitardo(ordineLavoro)
                                    ? `3px solid ${theme.palette.error.main}`
                                    : ordineLavoro.quantita_difforme
                                        ? `3px solid ${theme.palette.warning.main}`
                                        : "none",
                            }}
                        >
                            <CardContent sx={{py: 1, "&:last-child": {pb: 1}}}>
                                <Grid container spacing={1} sx={{alignItems: "center"}}>
                                    <Grid size={{xs: 0.5}}>
                                        <Link
                                            to={`${Routes_path.ORDINE_LAVORO}${ordineLavoro.id}`}
                                            style={{textDecoration: "none"}}
                                        >
                                            <Stack direction="row" spacing={0.5} sx={{alignItems: "center"}}>
                                                <Typography color="primary" variant="body2" fontWeight="bold">
                                                    {ordineLavoro.id}
                                                </Typography>
                                                {ordineLavoro.assegnazioni && ordineLavoro.assegnazioni.length > 0
                                                    && (ordineLavoro.valore_schedulato ?? 0) < (ordineLavoro.valore_confermato ?? 0)
                                                    && ordineLavoro.stato !== 'DONE'
                                                    && (
                                                        <Tooltip title="Schedulazione parziale">
                                                            <ScheduleIcon fontSize="small" color="warning"/>
                                                        </Tooltip>
                                                    )
                                                }
                                            </Stack>
                                        </Link>
                                    </Grid>
                                    <Grid size={{xs: 2.3}}>
                                        <Tooltip sx={{background: 'transparent'}}
                                                 title={
                                                     <AllegatiPreview previsione_id={ordineLavoro.previsione}/>
                                                 }>
                                            <Typography variant="body2" fontWeight="medium">
                                                {ordineLavoro.articolo_cliente ? ordineLavoro.codice_item + " (" + ordineLavoro.articolo_cliente + ")" : ordineLavoro.codice_item || "-"}
                                            </Typography>
                                        </Tooltip>
                                        <Typography variant="caption" color="text.secondary">
                                            {ordineLavoro.descrizione_item || ""}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 0.7}}>
                                        <Typography variant="body2">
                                            {ordineLavoro.stagione_display || "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 1}}>
                                        <Typography variant="body2">
                                            {ordineLavoro.mandatario_display || "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 1}}>
                                        <Typography variant="body2">
                                            {ordineLavoro.tecnica_display || "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 1}}>
                                        {ordineLavoro.stato && (
                                            <Chip
                                                label={ordineLavoro.stato_display || ordineLavoro.stato}
                                                color={getStatoOrdineLavoroColor(ordineLavoro.stato)}
                                                size="small"
                                            />
                                        )}
                                    </Grid>
                                    <Grid size={{xs: 1}}>
                                        <Typography variant="body2">
                                            {ordineLavoro?.stato === 'CONF'
                                                ? formatQta(ordineLavoro.qta_prevista)
                                                : `${formatQta(ordineLavoro.qta_arrivata)} (${formatQta(ordineLavoro.qta_prevista)})`
                                            }
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 1}}>
                                        <Typography variant="body2"
                                                    sx={{color: ordineLavoro.data_ricevimento ? 'inherit' : "info.main"}}>
                                            {ordineLavoro.data_ricevimento ?
                                                formatDate(ordineLavoro.data_ricevimento)
                                                :
                                                formatDate(ordineLavoro.data_arrivo_prevista)
                                            }
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 1}}>
                                        <Typography variant="body2">
                                            {formatDate(ordineLavoro.data_consegna_prevista)}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{xs: 0.5}}>
                                        {isOrdineLavoroInRitardo(ordineLavoro) && (
                                            <Stack direction="row" spacing={0.5} sx={{alignItems: "center"}}>
                                                <WarningAmberIcon fontSize="small" color="error"/>
                                                <Typography variant="body2" color="error">
                                                    {ordineLavoro.giorni_ritardo}g
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Grid>
                                    <Grid size={{xs: 1}}>
                                        {!!ordineLavoro?.quantita_difforme && (
                                            <Stack direction="row" spacing={0.5} sx={{alignItems: "center"}}>
                                                <WarningAmberIcon fontSize="small"
                                                                  color={ordineLavoro?.quantita_difforme >= 0 ? "info" : "error"}/>
                                                <Typography variant="body2"
                                                            color={ordineLavoro?.quantita_difforme >= 0 ? "info" : "error"}>
                                                    {formatQta(ordineLavoro?.quantita_difforme)}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Grid>
                                    <Grid size={{xs: 1}}>
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton
                                                component={Link}
                                                to={`${Routes_path.ORDINE_LAVORO}${ordineLavoro.id}`}
                                                size="small"
                                                color="primary"
                                            >
                                                <EditIcon fontSize="small"/>
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDelete(ordineLavoro)}
                                                size="small"
                                                color="error"
                                            >
                                                <DeleteIcon fontSize="small"/>
                                            </IconButton>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    ))}

                    {/* Paginazione */}
                    {(ordiniLavoroList.num_pages > 1 || ordiniLavoroList.count > 10) && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mt: 3,
                            }}
                        >
                            <FormControl sx={{minWidth: 120}} size="small">
                                <InputLabel>Record per pagina</InputLabel>
                                <Select
                                    value={filtri.numero_record || 20}
                                    label="Record per pagina"
                                    onChange={handleRecordsPerPageChange}
                                >
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={20}>20</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                </Select>
                            </FormControl>

                            <Pagination
                                count={ordiniLavoroList.num_pages}
                                page={filtri.page || 1}
                                onChange={handlePaginationChange}
                                color="primary"
                            />

                            <Typography variant="body2" color="text.secondary">
                                Totale: {ordiniLavoroList.count} record
                            </Typography>
                        </Box>
                    )}

                    {/* Empty state */}
                    {ordiniLavoroList.results.length === 0 &&
                        ordiniLavoroList.errorsStack.status !== ErrorStatusTypes.PENDING && (
                            <Card>
                                <CardContent>
                                    <Box sx={{textAlign: "center", py: 4}}>
                                        <WorkIcon
                                            sx={{
                                                fontSize: 64,
                                                color: "grey.400",
                                                mb: 2,
                                            }}
                                        />
                                        <Typography variant="h6" color="text.secondary">
                                            Nessun ordine di lavoro trovato
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Prova a modificare i filtri di ricerca
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        )}
                </Box>
            }

            {/* Menu filtro stati */}
            <Menu
                anchorEl={anchorElStatoFilter}
                open={Boolean(anchorElStatoFilter)}
                onClose={() => setAnchorElStatoFilter(null)}
            >
                <FormGroup sx={{px: 2}}>
                    {Object.entries(STATI_ORDINE_LAVORO).map(([key, value]) => (
                        <FormControlLabel
                            key={key}
                            control={
                                <Checkbox
                                    checked={statiSelezionati.includes(key)}
                                    onChange={() => toggleStato(key)}
                                    color="info"
                                />
                            }
                            label={value}
                        />
                    ))}
                </FormGroup>
                <Box sx={{px: 2, pb: 1}}>
                    <Button
                        onClick={() => {
                            setAnchorElStatoFilter(null);
                            setRefresh(true);
                        }}
                        variant="contained"
                        color="info"
                        size="small"
                        fullWidth
                    >
                        Applica
                    </Button>
                </Box>
            </Menu>

            {/* Menu filtro tecniche */}
            <Menu
                anchorEl={anchorElTecnicaFilter}
                open={Boolean(anchorElTecnicaFilter)}
                onClose={() => setAnchorElTecnicaFilter(null)}
            >
                <FormGroup sx={{px: 2}}>
                    {tecniche.map((tecnica) => (
                        <FormControlLabel
                            key={tecnica.id}
                            control={
                                <Checkbox
                                    checked={tecnicheSelezionate.includes(tecnica.id!)}
                                    onChange={() => toggleTecnica(tecnica.id!)}
                                    color="info"
                                />
                            }
                            label={tecnica.nome}
                        />
                    ))}
                </FormGroup>
                <Box sx={{px: 2, pb: 1}}>
                    <Button
                        onClick={() => {
                            setAnchorElTecnicaFilter(null);
                            setRefresh(true);
                        }}
                        variant="contained"
                        color="info"
                        size="small"
                        fullWidth
                    >
                        Applica
                    </Button>
                </Box>
            </Menu>

            {/* Loading */}
            <Backdrop
                open={ordiniLavoroList.errorsStack.status === ErrorStatusTypes.PENDING}
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
            >
                <CircularProgress color="inherit"/>
            </Backdrop>
        </Box>
    );
};

export default OrdiniLavoroPage;